import { buildPrompt } from '@/ai/utils/promptBuilder'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { createUserMessage, createSystemMessage } from '@/ai/utils/promptUtils'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildDescriptionValidatorPrompt = ({ workflow, attachments = [] }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Task',
    content: 'Decide whether the user input gives enough context to generate a useful business workflow. The input is a Workflow Description, optionally extended by Attached Files (filename and mime type only — you do not see file contents).'
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription, '')
  })

  if (attachments && attachments.length > 0) {
    promptSections.push({
      title: 'Attached Files',
      contentType: 'bullet',
      content: attachments.map((attachment) => `${attachment.name} (${attachment.mimeType})`)
    })
  }

  promptSections.push({
    title: 'Principles',
    contentType: 'bullet',
    content: [
      'You are a routing gate, NOT the workflow generator. The generator reads attachment contents directly. Your role is to forward the input unless there is a genuine problem. You do not need to "see" or "access" file contents — that is irrelevant to your decision.',
      'When ANY attachment is present, answer "yes" by default. Assume the attached file contains the business detail the description omits. The only valid reason to answer "no" with attachments present is a clear description/attachment mismatch — e.g. the description mentions "PDF" but only image files are attached, or it says "the screenshot" but no image is attached.',
      'When NO attachment is present, answer "yes" if the description plausibly describes a business process, project, or system. Answer "no" only for empty input, gibberish, or content unrelated to a workflow.',
      'NEVER state or imply that you cannot access file contents. Phrases like "the file is not accessible", "I cannot see the screenshot", "the contents are not visible" are forbidden in your output. They are irrelevant and confuse the user.',
      'Feedback (required when "no"): one short sentence (under 140 chars), specific to the actual input. Reference the user\'s wording or the attached filenames where applicable. Avoid generic phrasings.',
      'On mismatch ("no" with attachment present): in feedback, name what is attached vs. what the description references (e.g. "You mentioned a PDF but attached file is an image."). In suggestion, provide a corrected description that references the actual attached file.',
      'Suggestion (optional): a complete first-person workflow description in the user\'s voice that they could submit as-is — declarative, business-flavored. NOT advice to the user, NOT a meta-instruction (e.g. starting with "Please describe…"), NOT a request for clarification. Omit when you cannot produce such a rewrite.'
    ]
  })

  promptSections.push({
    title: 'Response',
    content: 'JSON only. No backticks. English regardless of input language. Match one of the shapes below.',
    spacingAfter: 1
  })

  const validShape = { answer: 'yes' }
  const invalidShape = { answer: 'no', feedback: 'string', suggestion: 'string (optional)' }

  promptSections.push({
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(validShape),
    spacingAfter: 1
  })

  promptSections.push({
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(invalidShape)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
